import React from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { CircularProgress } from '@material-ui/core';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { useUserContext } from 'providers/UserProvider';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { CompanyEntity, JobOrderEntity } from 'UI/constants/entityTypes';
import { UIStatus } from 'UI/constants/status';

import { JobOrderDetails } from '../../shared';
import { JOB_ORDER_INCLUDES } from '../CreateSendoutDrawer/CreateSendoutDrawer.constants';

import { LoaderContainer } from './CreateSendoverDrawer.styled';
import { SendoverForm } from './SendoverForm';

export const CreateSendoverDrawer = ({
  accountableId = null,
  jobOrderId = null,
  company = {},
  onClose = () => {},
  onSaved = () => {},
  onSaveAsDraft = () => {}
}) => {
  const [user] = useUserContext();
  const form = useForm();

  const { handleSubmit } = form;

  const isAccountable = user?.id === accountableId;
  const isCompanySignedOrClient = company.isSignedOrClient;

  const isCreationEnabled = isAccountable && isCompanySignedOrClient;

  const { state } = useFetchWithStatusV2({
    endpoint:
      jobOrderId && isCreationEnabled
        ? `${Endpoints.JobOrders}/${jobOrderId}?includes=${JOB_ORDER_INCLUDES}`
        : null,
    apiVersion: 2
  });

  const isFetching = isCreationEnabled && state.status === UIStatus.Loading;
  const jobOrder = state.results || {};

  const sendoverInfo = {
    jobOrder: {
      id: state.results?.id,
      title: JobOrderEntity.singular,
      name: state.results?.title
    },
    company: {
      id: state.results?.company?.id,
      title: CompanyEntity.singular,
      name: state.results?.company?.name
    }
  };

  const handleSave = formData => {
    console.log({ formData }); // TODO: remove this once submit logic is completed

    onSaved && onSaved({ closeAll: true });
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.cancel,
        variant: 'outlined',
        onClick: onClose
      },
      GridItemProps: { md: 3 }
    },
    {
      props: {
        text: strings.shared.ui.saveAsDraft,
        variant: 'outlined',
        onClick: onSaveAsDraft,
        disabled: isFetching
      },
      GridItemProps: { md: 3 }
    },
    {
      props: {
        text: strings.shared.ui.save,
        variant: 'contained',
        onClick: handleSubmit(handleSave),
        disabled: isFetching
      },
      GridItemProps: { md: 3 },
      mode: 'save'
    }
  ];

  return (
    <FPDrawer
      FPDrawerProps={{ open: true }}
      CardHeaderProps={{ title: strings.sendoutsV2.drawers.sendover.title }}
      onClose={onClose}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <When condition={!isCreationEnabled}>
        <EmptyPlaceholder
          subtitle={strings.formatString(strings.sendoutsV2.form.emptyPlaceholderText, {
            documentType: 'Sendover'
          })}
          height="100%"
        />
      </When>
      <When condition={isFetching}>
        <LoaderContainer>
          <CircularProgress />
        </LoaderContainer>
      </When>
      <When condition={isCreationEnabled && state.status === UIStatus.Success}>
        <FormContext {...form}>
          <JobOrderDetails company={sendoverInfo.company} jobOrder={sendoverInfo.jobOrder} />
          <SendoverForm jobOrder={jobOrder} />
        </FormContext>
      </When>
    </FPDrawer>
  );
};
